import { useMemo } from "react";
import { type RememberedUser } from "@components/RememberMe/contract";
import type { GetUserQuery } from "@generated/graphql";
import { type UserFragmentType } from "clientState/operations/fragments/userFragment/contract";
import { useRecognizedUser } from "clientState/RecognizedUserProvider/RecognizedUserProvider";
import { useQuery } from "@edreamsodigeo/frontend-api-client";

import { GET_USER } from "../operations/queries";
import { useRememberMe } from "../useRememberMe";
import buildUser from "./buildUser";
import type { UseUserResult } from "./contract";
import getUserStatuses from "./getUserStatuses";

export const useUser = (
  ignoreRememberMe = false,
  refetchOnWindowFocus = false,
): UseUserResult => {
  const { data, fetching, loading, queryKey } = useQuery<GetUserQuery>(
    GET_USER,
    {
      refetchOnWindowFocus,
    },
  );
  const recognizedUserState = useRecognizedUser();
  const [getRememberedUser] = useRememberMe();
  const rememberedUser = !ignoreRememberMe
    ? (getRememberedUser() as unknown as RememberedUser)
    : undefined;

  const user = useMemo(() => {
    const flattenedUser = buildUser({
      loading,
      recognizedUserState,
      rememberedUser,
      user: data?.getUser as UserFragmentType,
    });
    const statuses = getUserStatuses({
      fetching,
      ...flattenedUser,
    });

    return {
      ...flattenedUser,
      ...statuses,
    };
  }, [fetching, data?.getUser, recognizedUserState, rememberedUser, loading]);

  return { user, userQueryKey: queryKey };
};
